import React from "react";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";
import { Wrench, Coins } from "lucide-react";
import { formatPromptCount } from "@/utils/ai/utils";
interface ChatMessageUsageIndicatorProps {
  promptsConsumed?: number;
  toolCallsCount?: number;
  inputTokens?: number;
  outputTokens?: number;
}

export const ChatMessageUsageIndicator: React.FC<
  ChatMessageUsageIndicatorProps
> = ({ promptsConsumed, toolCallsCount, inputTokens, outputTokens }) => {
  const { remainingUsage } = useUsageLimits();
  const totalTokens = (inputTokens || 0) + (outputTokens || 0);

  // Determine if we should show tokens or credits based on subscription tier
  // Show tokens for users NOT on free tier and NOT starting with 'new'
  // Show credits for everyone else (free tier and new_* tiers)
  const shouldShowTokens =
    remainingUsage.subscriptionTier &&
    remainingUsage.subscriptionTier !== "free" &&
    !remainingUsage.subscriptionTier.startsWith("new");

  const hasMetrics =
    (toolCallsCount && toolCallsCount > 0) ||
    (shouldShowTokens ? totalTokens > 0 : (promptsConsumed ?? 0) > 0);

  if (!hasMetrics) {
    return null;
  }

  const formatTokenCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  return (
    <div className="flex flex-wrap gap-3 text-xs text-secondary-n-500">
      {shouldShowTokens
        ? // Show tokens for old tier users (not free, not new_*)
          totalTokens > 0 && (
            <span
              className="flex items-center gap-1.5 cursor-help"
              data-tooltip-id="tooltip"
              data-tooltip-content="Tokens consumed by AI"
            >
              <Coins className="w-3.5 h-3.5" />
              {formatTokenCount(totalTokens)} tokens
            </span>
          )
        : // Show prompts for free and new_* tier users
          (promptsConsumed ?? 0) > 0 && (
            <span className="flex items-center gap-1.5">
              <Coins className="w-3.5 h-3.5" />
              {formatPromptCount(promptsConsumed!)} credit
              {promptsConsumed === 1 ? "" : "s"}
            </span>
          )}
      {toolCallsCount != null && toolCallsCount > 0 && (
        <span className="flex items-center gap-1.5">
          <Wrench className="w-3.5 h-3.5" />
          {toolCallsCount} tool call{toolCallsCount !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
};
